<script setup lang="ts">
import PieChart from '@/components/PieChart.vue';
import { useStore } from '@/stores/main';
import { computed, ref, watchEffect } from 'vue';
import { watch } from 'vue';

const store = useStore();
const props = defineProps<{
  questions: any[];
  factors: any;
}>();

// Debug logging to track profile updates
watch(() => store.profile, (newProfile) => {
  // console.log('Profile updated:', newProfile);
}, { deep: true });

// Debug logging for props
// watchEffect(() => {
//   console.log('WorkResult received factors:', props.factors);
//   console.log('WorkResult received questions:', props.questions);
// });

const questionResults = computed(() => {
  const results = [];

  for (const question of props.questions) {
    const factor = props.factors?.[question.category_id];
    // Debug each factor to identify issues
    // console.log(`Processing question ${question.category_id}:`, factor);
    
    if (factor == null) {
      results.push({
        category: question.category_id,
        label: question.label,
        answered: false,
        value: null,
        status: 'unknown',
      });
      continue;
    }

    // Prioritize risk_value over value when available
    const displayValue = factor.risk_value !== undefined ? factor.risk_value : factor.value;
    
    results.push({
      category: question.category_id,
      label: question.label,
      answered: displayValue !== null && displayValue !== '',
      value: displayValue,
      status: factor.status,
    });
  }

  // console.log('Calculated questionResults:', results);
  return results;
});

const selectedSlice = ref<number | null>(null);

const segmentColors = computed(() => {
  return questionResults.value.map(sr => {
    // Using displayValue for color determination
    return fillColor(+sr.value || 0);
  });
});

const segmentLabels = computed(() => {
  let index = 1;
  return questionResults.value.map(sr => {
    return '' + index++;
  });
});

const fillColors = [
  'fill-white',
  'fill-red-400',
  'fill-orange-400',
  'fill-amber-300',
  'fill-lime-300',
  'fill-green-400',
];

const bgColors = [
  'bg-white',
  'bg-red-400',
  'bg-orange-400',
  'bg-amber-300',
  'bg-lime-300',
  'bg-green-400',
];

function fillColor(status: number): string {
  // Ensure status is within valid range
  if (status < 0 || status >= fillColors.length) {
    console.warn(`Invalid status value: ${status}, defaulting to 0`);
    status = 0;
  }
  return fillColors[status];
}

function highlightSwatch(index: number) {
  if (selectedSlice.value === null) {
    return null;
  }

  return +questionResults.value[selectedSlice.value].value === index
    ? 'scale-125 border border-solid border-white'
    : null;
}
</script>

<template>
  <div class="results-container">
    <!-- Debugging section - can be removed in production -->
    <div v-if="false" class="mb-4 p-2 bg-gray-100 rounded text-xs">
      <pre>{{ JSON.stringify(questionResults, null, 2) }}</pre>
    </div>
    
    <div class="flex flex-col lg:flex-row space-y-6 lg:space-x-10 lg:space-y-0 items-center justify-center">
      <div class="chart-block shift-right">
        <div class="chart-container relative">
        <PieChart
          v-model="selectedSlice"
          :colors="segmentColors"
          :labels="segmentLabels"
          :segments="questionResults.length"
        />

        </div>
        <div class="legend flex justify-center space-x-2 items-center text-base">
          <div class="flex-1 flex text-sm items-center">
            <div
              class="flex-1 border-0 border-t border-solid border-black/30"
            ></div>
            <div class="px-1">Risk</div>
          </div>
          <div class="flex flex-col items-center">
            <div class="flex space-x-2">
              <span
                :class="[
                  'flex items-center justify-center w-5 h-5 outline outline-1 rounded-full text-sm transition',
                  bgColors[1],
                  highlightSwatch(1),
                ]"
                >1</span
              >
              <span
                :class="[
                  'flex items-center justify-center w-5 h-5 outline outline-1 rounded-full text-sm transition',
                  bgColors[2],
                  highlightSwatch(2),
                ]"
                >2</span
              >
            </div>
          </div>
          <div class="flex flex-col items-center">
            <div class="flex space-x-2">
              <span
                :class="[
                  'flex items-center justify-center w-5 h-5 outline outline-1 rounded-full text-sm transition',
                  bgColors[3],
                  highlightSwatch(3),
                ]"
                >3</span
              >
              <span
                :class="[
                  'flex items-center justify-center w-5 h-5 outline outline-1 rounded-full text-sm transition',
                  bgColors[4],
                  highlightSwatch(4),
                ]"
                >4</span
              >
              <span
                :class="[
                  'flex items-center justify-center w-5 h-5 outline outline-1 rounded-full text-sm transition',
                  bgColors[5],
                  highlightSwatch(5),
                ]"
                >5</span
              >
            </div>
          </div>
          <div class="flex-1 flex text-sm items-center">
            <div class="px-1">Frisk</div>
            <div
              class="flex-1 border-0 border-t border-solid border-black/30"
            ></div>
          </div>
        </div>
      </div>

      <div class="details-outer">
        <transition
          enter-from-class="!grid-rows-[0fr]"
          leave-to-class="!grid-rows-[0fr]"
        >
          <div
            v-if="selectedSlice !== null"
            class="grid grid-rows-[1fr] transition-[grid-template-rows] duration-[400ms]"
          >
            <div class="overflow-hidden">
              <h3
                class="my-0 text-yellow-darker text-base"
                v-text="questionResults[selectedSlice].label"
              ></h3>

              <div class="text-sm font-light mt-1">
                <template
                  v-if="
                    questionResults[selectedSlice].status === 'profile.healthy' ||
                    questionResults[selectedSlice].status === 'healthy'
                  "
                >
                  <div class="text-base font-medium mb-1">Frisk</div>
                  <div>
                    Du är på rätt väg för att må bra i framtiden. Bygg vidare på
                    det!
                  </div>
                </template>
                <template v-else>
                  <div class="text-base font-medium mb-1">Risk</div>
                  <div>Se upp för kommande ohälsa.</div>
                </template>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>

  <div class="h-8"></div>
</template>

<style scoped lang="postcss">
.results-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.shift-right {
  margin-right: 0;
}

@media (min-width: 1024px) {
  .shift-right {
    margin-right: 2rem;
  }
}

.chart-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.chart-container {
  width: 280px;
  height: 280px;
}

@media (min-width: 768px) {
  .chart-container { 
    width: 340px; 
    height: 340px; 
  }
}

.legend { 
  margin-top: 0.5rem; 
}

.details-outer {
  width: 100%;
  max-width: 520px;
}
</style>